import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';

import { FEATURE_NAMES } from '@/hooks/user';
import transactionModel from '@/models/transaction';
import routes from '@/routes';
import { showError, showSuccess } from '@/utils';
import {
	checkIfFileNeedsPassword,
	isInvalidPasswordError
} from '@/utils/download';
import { isSimdAvailable } from '@/utils/wasm';
import { decrypt } from '@/utils/worker';

import { printPdfFile } from './utils';

export function usePrintModal({ onOpenChange, id, open }) {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const fileBytesRef = useRef(null);
	const isEditor = useMatch(routes.editor);
	const passwordEditor = useSelector(state => state.editor.password);
	const [passwordModalOpen, setPasswordModalOpen] = useState(false);
	const [validatingPassword, setValidatingPassword] = useState(false);
	const [invalidPassword, setInvalidPassword] = useState(false);
	const abortControllerRef = useRef();

	useEffect(() => {
		if (open) {
			abortControllerRef.current = new AbortController();
		}
	}, [open]);

	async function handlePrintFile(password) {
		try {
			setLoading(true);
			let file = fileBytesRef.current;

			if (password) {
				file = await decrypt(fileBytesRef.current.buffer, password);
			}

			printPdfFile(file);
			onOpenChange(false);
			setLoading(false);
			showSuccess(t('myDocuments.toast.printDocumentSuccess'));
		} catch (error) {
			showError(error);
		} finally {
			setValidatingPassword(false);
			setLoading(false);
		}
	}

	async function handlePrintDocument() {
		setLoading(true);

		if (isEditor) {
			const { default: PDFViewerApplication } = await import(
				'@/pdfjsWeb/pdf_viewer_application'
			);

			fileBytesRef.current = await PDFViewerApplication.getDocumentData();

			handlePrintFile(passwordEditor);
			return;
		}

		try {
			const url = transactionModel.downloadUrl(id, 'pdf');

			const response = await fetch(url, {
				signal: abortControllerRef.current?.signal
			});
			const fileArrayBuffer = await response.arrayBuffer();

			fileBytesRef.current = new Uint8Array(fileArrayBuffer);
			const hasPassword = await checkIfFileNeedsPassword(
				fileBytesRef.current
			);

			if (hasPassword) {
				if (!isSimdAvailable()) {
					showError(
						t(
							'myDocuments.modals.printDocument.passwordProtectedUnavailable'
						)
					);
					return onOpenChange(false);
				}
				onOpenChange(false);
				setPasswordModalOpen(true);
			} else {
				handlePrintFile();
			}
		} catch (error) {
			if (error.name !== 'AbortError') {
				showError(error);
			}
		} finally {
			setLoading(false);
		}
	}

	async function handleSubmitPassword(password) {
		try {
			const { loadPDFWasm, PDFWasmPdfium } = await import('@/pdfe-wasm');
			setValidatingPassword(true);
			setInvalidPassword(false);
			await loadPDFWasm();
			const pdf = new PDFWasmPdfium(fileBytesRef.current, password);
			pdf.dispose();
			setPasswordModalOpen(false);
			onOpenChange(FEATURE_NAMES.PRINT);
			handlePrintFile(password);
		} catch (error) {
			setValidatingPassword(false);
			if (isInvalidPasswordError(error)) {
				return setInvalidPassword(true);
			}
			showError(error);
		}
	}

	return {
		passwordModalProps: {
			open: passwordModalOpen,
			invalidPassword,
			loading: validatingPassword,
			onSubmit: handleSubmitPassword,
			onOpenChange: setPasswordModalOpen
		},
		onSubmit: handlePrintDocument,
		loading,
		onOpenChange: value => {
			if (!value) {
				abortControllerRef.current?.abort();
			}
			onOpenChange(value);
		}
	};
}
